import type { TableNode } from '..';
import type { NodeColumns } from '../table-store';
import { isGroupRow, isSubRow } from './group-row-guards';

type OrderRow = Pick<
  TableNode,
  NodeColumns.id | NodeColumns.nodeType | NodeColumns.settings
>;

/**
 * PSTUDIO-3867 — canonical group-contiguous render order.
 *
 * Given rows already sorted by their numeric `order`, return a new ordering in
 * which every group's member rows render immediately after their group header,
 * in their incoming (order-sorted) sequence, with no foreign row interleaved.
 * A member whose header is absent from the list (e.g. a search-filtered orphan)
 * is left in place.
 *
 * The group-boundary box in draggable-row.scss is drawn by CSS DOM-adjacency
 * selectors (`.c-editor-table-tr--group:has(+ .c-editor-table-tr--subrow)` and
 * `+ .c-editor-table-tr--subrow`). Those selectors only match when members are
 * the header's immediate DOM siblings — so guaranteeing contiguity HERE, at the
 * single order source every consumer derives from, makes the boundary correct
 * for every dataset regardless of `order` drift after reorder/undo/paste.
 *
 * Idempotent and a no-op (returns the SAME array reference) when the input is
 * already contiguous, so no reordering ever happens for well-formed data. A
 * table with no group rows exits in a single pass (the early-out below) with
 * zero extra allocation — the common case on large workspaces.
 */
export function arrangeGroupsContiguously<T extends OrderRow>(
  rows: readonly T[]
): T[] {
  // Collect, in incoming order, the present members of each present group.
  const presentGroupIds = new Set<string>();
  const membersByGroup = new Map<string, T[]>();
  for (const row of rows) {
    if (isGroupRow(row)) presentGroupIds.add(row.id);
  }
  // Group-free tables (the majority, including large ones) can never need
  // rearranging — return the input untouched without building `result`.
  if (presentGroupIds.size === 0) return rows as T[];
  for (const row of rows) {
    if (isGroupRow(row)) continue;
    // Membership is decided by the member's own `groupId` back-pointer (not the
    // header's `subRows` list) — the two are kept in lockstep upstream, and
    // reading groupId here means a one-sided row is still placed with its group.
    const gid = row.settings?.groupId;
    if (gid && presentGroupIds.has(gid)) {
      const list = membersByGroup.get(gid);
      if (list) list.push(row);
      else membersByGroup.set(gid, [row]);
    }
  }

  const result: T[] = [];
  for (const row of rows) {
    if (isGroupRow(row)) {
      result.push(row);
      const members = membersByGroup.get(row.id);
      if (members) {
        for (const m of members) result.push(m);
      }
      continue;
    }
    // A member whose header is present is emitted with its header block above.
    const gid = row.settings?.groupId;
    if (gid && presentGroupIds.has(gid)) continue;
    // Plain row or orphan member (header absent) — keep in place.
    result.push(row);
  }

  // No-op fast path: identity preserved when nothing moved (referential
  // stability matters — this feeds a MobX computed; a fresh array every call
  // would defeat memoization and re-run useReactTable's row model).
  if (result.length === rows.length) {
    let same = true;
    for (let i = 0; i < result.length; i += 1) {
      if (result[i] !== rows[i]) {
        same = false;
        break;
      }
    }
    if (same) return rows as T[];
  }
  return result;
}

/**
 * PSTUDIO-3867 — ordering/adjacency invariant. Over a list in render order,
 * returns one human-readable string per group whose members are NOT a
 * contiguous block immediately after the header (foreign row interleaved, or a
 * member above its header). Empty array = contiguous. Orphan members (header
 * absent) are not flagged. Pure; used by dev assertions and regression tests.
 */
export function collectGroupOrderingViolations(
  rows: ReadonlyArray<OrderRow>
): string[] {
  const violations: string[] = [];
  const indexById = new Map<string, number>();
  rows.forEach((r, i) => {
    if (!indexById.has(r.id)) indexById.set(r.id, i);
  });

  for (let i = 0; i < rows.length; i += 1) {
    const header = rows[i];
    if (!isGroupRow(header)) continue;
    const subRows = header.settings?.subRows ?? [];
    const presentMembers = subRows.filter((id) => indexById.has(id));
    if (presentMembers.length === 0) continue;

    // Every present member must sit in the contiguous window right after the
    // header, and every row in that window must be a member of this group.
    const windowEnd = i + presentMembers.length; // exclusive upper bound below
    for (let j = i + 1; j <= windowEnd && j < rows.length; j += 1) {
      const r = rows[j];
      if (!isSubRow(r) || r.settings?.groupId !== header.id) {
        violations.push(
          `group ${header.id} members are not contiguous after the header (foreign row ${r.id} at position ${j})`
        );
        break;
      }
    }
    // A member positioned BEFORE the header.
    for (const id of presentMembers) {
      if ((indexById.get(id) ?? -1) < i) {
        violations.push(
          `group ${header.id} member ${id} renders before its header`
        );
        break;
      }
    }
  }

  return violations;
}
